import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
// form
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
// @mui
import { LoadingButton } from "@mui/lab";
import { Alert, CircularProgress, IconButton, InputAdornment, Stack } from "@mui/material";
import { Box } from "@mui/system";
// routes
import { sendOtp, sendOtpIdNumber, verifyEmailAddress } from "src/api/megs-api-api";
import { PATH_AUTH } from "../../routes/paths";
// auth
import { useAuthContext } from "../../auth/useAuthContext";
// components
import FormProvider, { RHFTextField } from "../../components/hook-form";
import Iconify from "../../components/iconify";
import { useSnackbar } from "../../components/snackbar";
// ----------------------------------------------------------------------

export default function AdminAuthLoginForm() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuthContext();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    username: Yup.string().required("Username is required"),
    password: Yup.string().required("Password is required"),
  });

  const defaultValues = {
    username: "",
    password: "",
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;

  const onSubmit = async (data) => {
    try {
      await login(data.username.trim(), data.password.trim());
    } catch (error) {
      console.error(error);
      reset();
      setError("afterSubmit", {
        type: "manual",
        message: error.message,
      });
    }
  };

  const handleVerifyEmail = async (email) => {
    const response = await verifyEmailAddress(email);
    if (response.status_code !== 1001) {
      enqueueSnackbar(response.data, {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      setLoading(false);
      
    }
  };

  const handleForgotPasswordWithIdNumber = async (idNumber) => {
    console.log("Handling password reset for ID number:", idNumber);

    setLoading(true);

    // TODO: Add ID number verification if needed
    // const userResponse = await verifyIdNumber(idNumber);

    // Send OTP via ID number
    const response = await sendOtpIdNumber(idNumber);
    if (response.status_code !== 1001) {
      enqueueSnackbar(response.data, {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      setLoading(false);
      return;
    } 
      enqueueSnackbar("An OTP has been sent to your phone number.", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      setLoading(false);
    

    // Navigate to reset password page with ID number
    navigate(PATH_AUTH.resetPassword, { state: { idNumber } });
  };

  const handleForgotPasswordWithEmail = async (email) => {
    setLoading(true);
    await handleVerifyEmail(email);
    const response = await sendOtp(email);
    if (response.status_code !== 1001) {
      enqueueSnackbar(response.data, {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      setLoading(false);
      return;
    } 
      enqueueSnackbar("OTP sent to your email address.", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      setLoading(false);
    

    navigate(PATH_AUTH.resetPassword, { state: { email } });
  };

  const handleForgotPassword = async () => {
    const username = methods.getValues("username")?.trim();
    if (!username) {
      enqueueSnackbar("Please complete the username to reset your password.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      setTimeout(() => {
        closeSnackbar();
      }, 4000);
      return;
    }

    // Check if the username is a valid email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(username)) {
      // Handle email-based password reset (existing flow)
      await handleForgotPasswordWithEmail(username);
    } else {
      // Handle ID number-based password reset (new flow)
      await handleForgotPasswordWithIdNumber(username);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField name="username" label="Username" autoComplete="username" />

        <RHFTextField
          name="password"
          label="Password"
          autoComplete="current-password"
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <LoadingButton
        fullWidth
        color="inherit"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitSuccessful || isSubmitting}
        sx={{
          mt: 3,
          bgcolor: "text.primary",
          color: (theme) => (theme.palette.mode === "light" ? "common.white" : "grey.800"),
          "&:hover": {
            bgcolor: "text.primary",
            color: (theme) => (theme.palette.mode === "light" ? "common.white" : "grey.800"),
          },
        }}
      >
        Login
      </LoadingButton>
      {loading ? (
        <Box
          sx={{
            pt: 2,
            mr: 1,
            alignSelf: "center",
          }}
        >
          <CircularProgress size={20} color="primary" />
        </Box>
      ) : (
        <Stack spacing={2} sx={{ mb: 5, display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
          <LoadingButton onClick={handleForgotPassword} sx={{ marginTop: "16px !important", mx: "auto", display: "block", cursor: "pointer" }}>
            Forgot password?
          </LoadingButton>
        </Stack>
      )}
      {window.location.href.includes("tuppconnect") && (
        <LoadingButton
          fullWidth
          color="inherit"
          size="large"
          variant="contained"
          sx={{
            mt: 3,
            bgcolor: "text.primary",
            color: (theme) => (theme.palette.mode === "light" ? "common.white" : "grey.800"),
            "&:hover": {
              bgcolor: "text.primary",
              color: (theme) => (theme.palette.mode === "light" ? "common.white" : "grey.800"),
            },
          }}
          onClick={() => navigate("/tupperware/onboarding")}
        >
          Register
        </LoadingButton>
      )}
    </FormProvider>
  );
}
